import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  BarChart3, 
  Calendar,
  Users,
  ClipboardCheck,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Filter,
  Loader2,
  Clock,
  XCircle,
  Package,
  DollarSign,
  TrendingDown,
  MapPin,
  Heart,
  Stethoscope,
  Baby,
  Download,
  FileSpreadsheet
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

// Colors for charts
const COLORS = {
  dentroPrazo: '#7FB285',
  atrasadas: '#E63946',
  vencidasAbertas: '#FFB703',
  internacoes: '#3B82F6',
  altas: '#7FB285',
  tarefas: '#8B5CF6',
  diarias: '#6366f1'
};

const MOTIVO_COLORS = {
  'Alta clínica': '#7FB285',
  'Transferência': '#219EBC',
  'Óbito': '#3D3D3D',
  'Desistência': '#FFB703',
  'Outro': '#8338EC'
};

// Colors for origem pie chart
const ORIGEM_CHART_COLORS = {
  HGR: '#3b82f6',
  HMI: '#f59e0b',
  PACS: '#06b6d4',
  cardiologia: '#ef4444',
  paliativo: '#a855f7',
  clinico: '#3b82f6',
  cirurgia: '#f97316',
  completar_atb: '#22c55e',
  nao_informado: '#6b7280'
};

const ChartSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="mt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full justify-between"
      >
        <span className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" />
          Ver gráficos
        </span>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>
      {isOpen && (
        <div className="mt-4 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
};

// Custom tooltip component (moved outside to avoid re-creation)
const CustomTooltip = ({ active, payload, label, clickHint = true }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
        <p className="font-medium">{label || payload[0]?.name}</p>
        <p className="text-sm text-muted-foreground">
          Valor: <span className="font-semibold text-primary">{payload[0]?.value}</span>
        </p>
        {clickHint && (
          <p className="text-xs text-blue-500 mt-1">Clique para ver detalhes</p>
        )}
      </div>
    );
  }
  return null;
};

const Estatisticas = () => {
  const { api, canViewServiceRevenue, canViewServicePayout, can } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [data, setData] = useState(null);
  const [estoqueData, setEstoqueData] = useState(null);
  const [servicosData, setServicosData] = useState(null);
  const [origemData, setOrigemData] = useState(null);
  
  // Filter state (pending - not applied yet)
  const [pendingFilters, setPendingFilters] = useState({
    periodo: '7d',
    agrupamento: 'dia'
  });
  
  // Applied filters (what's currently shown)
  const [appliedFilters, setAppliedFilters] = useState({
    periodo: '7d',
    agrupamento: 'dia'
  });
  
  // Track if filters have changed
  const filtersChanged = 
    pendingFilters.periodo !== appliedFilters.periodo ||
    pendingFilters.agrupamento !== appliedFilters.agrupamento;

  const fetchEstatisticas = useCallback(async (filters) => {
    try {
      setApplying(true);
      const [statsRes, estoqueRes, servicosRes, origemRes] = await Promise.all([
        api.get(`/estatisticas?periodo=${filters.periodo}&agrupamento=${filters.agrupamento}`),
        api.get(`/estoque/analytics?periodo=${filters.periodo}`).catch(() => ({ data: null })),
        api.get(`/servicos/analytics?periodo=${filters.periodo}`).catch(() => ({ data: null })),
        api.get(`/internacoes/relatorio/por-origem?incluir_altas=false`).catch(() => ({ data: null }))
      ]);
      setData(statsRes.data);
      setEstoqueData(estoqueRes.data);
      setServicosData(servicosRes.data);
      setOrigemData(origemRes.data);
      setAppliedFilters(filters);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setApplying(false);
      setLoading(false);
    }
  }, [api]);

  // Initial load
  useEffect(() => {
    fetchEstatisticas(pendingFilters);
  }, []);

  const handleApplyFilters = () => {
    fetchEstatisticas(pendingFilters);
  };

  const handleFilterChange = (key, value) => {
    setPendingFilters(prev => ({ ...prev, [key]: value }));
  };

  // Chart click handlers for drill-down navigation
  const handleInternacoesClick = (data) => {
    if (data?.activePayload) {
      const label = data.activePayload[0]?.payload?.label;
      toast.info(`Visualizando internações de ${label}`);
      navigate('/lista');
    }
  };

  const handleAltasClick = (data) => {
    if (data?.name) {
      const motivoMap = {
        'Alta clínica': 'alta_clinica',
        'Transferência': 'transferencia',
        'Óbito': 'obito',
        'Desistência': 'desistencia',
        'Outro': 'outro'
      };
      const motivo = motivoMap[data.name] || '';
      toast.info(`Visualizando altas: ${data.name}`);
      navigate(`/lista?tab=altas&motivo=${motivo}`);
    }
  };

  const handleTarefasClick = (data) => {
    if (data?.name) {
      const statusMap = {
        'Dentro do prazo': 'concluida',
        'Concluídas atrasadas': 'concluida',
        'Em aberto vencidas': 'vencidas'
      };
      const filter = statusMap[data.name];
      if (filter === 'vencidas') {
        toast.info('Visualizando tarefas vencidas em aberto');
        navigate('/tarefas?vencidas=true');
      } else {
        toast.info(`Visualizando tarefas: ${data.name}`);
        navigate(`/tarefas?status=${filter}`);
      }
    }
  };

  // Get trend data based on agrupamento
  const getTrendData = (section) => {
    if (!data) return [];
    const sectionData = data[section];
    if (!sectionData) return [];
    
    if (appliedFilters.agrupamento === 'semana') {
      return sectionData.trend_semanal || sectionData.trend || [];
    }
    return sectionData.trend || [];
  };

  // Tarefas distribution data for pie chart (3 categories)
  const tarefasDistribuicaoData = data ? [
    { name: 'Dentro do prazo', value: data.tarefas.dentro_prazo, color: COLORS.dentroPrazo },
    { name: 'Concluídas atrasadas', value: data.tarefas.atrasadas, color: COLORS.atrasadas },
    { name: 'Em aberto vencidas', value: data.tarefas.vencidas_abertas, color: COLORS.vencidasAbertas }
  ].filter(d => d.value > 0) : [];

  if (loading && !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-24" />
          <div className="grid gap-6 md:grid-cols-3">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="estatisticas-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
                Estatísticas
              </h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Métricas e indicadores do sistema
            </p>
          </div>
        </div>

        {/* Filter Bar */}
        <Card className="border-2 border-dashed">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Filter className="h-5 w-5" />
                <span className="font-medium">Filtros</span>
              </div>
              
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Período Filter */}
                <div className="space-y-1.5">
                  <Label htmlFor="periodo" className="text-sm">Período</Label>
                  <Select 
                    value={pendingFilters.periodo} 
                    onValueChange={(v) => handleFilterChange('periodo', v)}
                  >
                    <SelectTrigger id="periodo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7d">Últimos 7 dias</SelectItem>
                      <SelectItem value="mes">Mês atual</SelectItem>
                      <SelectItem value="sempre">Sempre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Agrupamento Filter */}
                <div className="space-y-1.5">
                  <Label htmlFor="agrupamento" className="text-sm">Agrupamento</Label>
                  <Select 
                    value={pendingFilters.agrupamento} 
                    onValueChange={(v) => handleFilterChange('agrupamento', v)}
                  >
                    <SelectTrigger id="agrupamento">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dia">Dia</SelectItem>
                      <SelectItem value="semana">Semana</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Applied Period Display */}
                <div className="space-y-1.5">
                  <Label className="text-sm">Período aplicado</Label>
                  <div className="h-10 flex items-center">
                    {data?.periodo && (
                      <Badge variant="outline" className="text-sm">
                        <Calendar className="h-3 w-3 mr-1" />
                        {data.periodo.inicio} a {data.periodo.fim}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Apply Filters Button */}
              <div className="flex-shrink-0">
                <Button 
                  onClick={handleApplyFilters}
                  disabled={applying || !filtersChanged}
                  className="w-full lg:w-auto"
                  variant={filtersChanged ? "default" : "outline"}
                >
                  {applying ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <Filter className="h-4 w-4 mr-2" />
                      APLICAR FILTROS
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {filtersChanged && (
              <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                Filtros alterados. Clique em &quot;APLICAR FILTROS&quot; para atualizar.
              </p>
            )}
          </CardContent>
        </Card>

        {data && (
          <>
            {/* Internações Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      Internações
                    </CardTitle>
                    <CardDescription>
                      Admissões criadas no período selecionado
                    </CardDescription>
                  </div>
                  {can('exportar_internacoes') && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/internacoes/relatorio/geral/export?formato=csv&periodo=${appliedFilters.periodo}`, '_blank');
                        toast.success('Download CSV iniciado');
                      }}
                      data-testid="export-internacoes-csv"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/internacoes/relatorio/geral/export?formato=excel&periodo=${appliedFilters.periodo}`, '_blank');
                        toast.success('Download Excel iniciado');
                      }}
                      data-testid="export-internacoes-excel"
                    >
                      <FileSpreadsheet className="h-4 w-4 mr-1" />
                      Excel
                    </Button>
                  </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="text-center p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex-1">
                    <p className="text-4xl font-bold text-blue-600">{data.internacoes.total}</p>
                    <p className="text-sm text-muted-foreground mt-1">Total de internações</p>
                  </div>
                </div>
                
                <ChartSection title="Ver gráficos">
                  <div className="h-64">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Tendência de internações ({appliedFilters.agrupamento === 'semana' ? 'semanal' : 'diário'})
                    </p>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={getTrendData('internacoes')}
                        onClick={handleInternacoesClick}
                        className="cursor-pointer"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis allowDecimals={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey="value" 
                          fill={COLORS.internacoes} 
                          radius={[4, 4, 0, 0]}
                          className="hover:opacity-80 transition-opacity cursor-pointer"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ChartSection>
              </CardContent>
            </Card>

            {/* Altas Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Altas
                </CardTitle>
                <CardDescription>
                  Altas efetivadas e motivos no período selecionado
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-4xl font-bold text-green-600">{data.altas.total}</p>
                    <p className="text-sm text-muted-foreground mt-1">Total de altas</p>
                  </div>
                  
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Por motivo:</p>
                    {data.altas.por_motivo.length > 0 ? (
                      <div className="space-y-2">
                        {data.altas.por_motivo.map((item, idx) => (
                          <div key={idx} className="flex justify-between items-center">
                            <span className="text-sm">{item.motivo}</span>
                            <Badge variant="outline">{item.count}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Nenhuma alta no período</p>
                    )}
                  </div>
                </div>
                
                <ChartSection title="Ver gráficos">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="h-64">
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Tendência de altas ({appliedFilters.agrupamento === 'semana' ? 'semanal' : 'diário'})
                      </p>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={getTrendData('altas')}
                          onClick={() => navigate('/lista?tab=altas')}
                          className="cursor-pointer"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                          <YAxis allowDecimals={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar 
                            dataKey="value" 
                            fill={COLORS.altas} 
                            radius={[4, 4, 0, 0]}
                            className="hover:opacity-80 transition-opacity cursor-pointer"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {data.altas.por_motivo.length > 0 && (
                      <div className="h-64">
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          Distribuição por motivo de alta
                        </p>
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={data.altas.por_motivo}
                              dataKey="count"
                              nameKey="motivo"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                              labelLine={true}
                              onClick={handleAltasClick}
                              className="cursor-pointer"
                            >
                              {data.altas.por_motivo.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={MOTIVO_COLORS[entry.motivo] || COLORS.internacoes}
                                  className="hover:opacity-80 transition-opacity cursor-pointer"
                                />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </ChartSection>
              </CardContent>
            </Card>

            {/* Tarefas Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardCheck className="h-5 w-5 text-purple-600" />
                  Tarefas
                </CardTitle>
                <CardDescription>
                  Produtividade e SLA de tarefas no período selecionado
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Stats Cards */}
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                  <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                    <p className="text-3xl font-bold text-purple-600">{data.tarefas.total_concluidas}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total concluídas</p>
                  </div>
                  
                  <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <p className="text-3xl font-bold text-green-600">{data.tarefas.dentro_prazo}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Dentro do prazo</p>
                  </div>
                  
                  <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="flex items-center justify-center gap-1">
                      <Clock className="h-4 w-4 text-red-600" />
                      <p className="text-3xl font-bold text-red-600">{data.tarefas.atrasadas}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Concluídas atrasadas</p>
                  </div>
                  
                  <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border-2 border-amber-200 dark:border-amber-800">
                    <div className="flex items-center justify-center gap-1">
                      <XCircle className="h-4 w-4 text-amber-600" />
                      <p className="text-3xl font-bold text-amber-600">{data.tarefas.vencidas_abertas}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 font-medium">Em aberto vencidas</p>
                  </div>
                </div>
                
                {/* SLA Summary */}
                {data.tarefas.total_concluidas > 0 && (
                  <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Taxa de cumprimento de SLA (concluídas):</span>
                      <Badge 
                        className={
                          (data.tarefas.dentro_prazo / data.tarefas.total_concluidas) >= 0.8
                            ? 'bg-green-100 text-green-700'
                            : (data.tarefas.dentro_prazo / data.tarefas.total_concluidas) >= 0.5
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                        }
                      >
                        {((data.tarefas.dentro_prazo / data.tarefas.total_concluidas) * 100).toFixed(1)}%
                      </Badge>
                    </div>
                  </div>
                )}

                {/* Warning for overdue open tasks */}
                {data.tarefas.vencidas_abertas > 0 && (
                  <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-700 dark:text-amber-400">
                          Atenção: {data.tarefas.vencidas_abertas} tarefa(s) vencida(s) em aberto
                        </p>
                        <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">
                          Essas tarefas estão com a data prevista ultrapassada e ainda não foram concluídas.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <ChartSection title="Ver gráficos">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="h-64">
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Tarefas concluídas ({appliedFilters.agrupamento === 'semana' ? 'semanal' : 'diário'})
                      </p>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={getTrendData('tarefas')}
                          onClick={() => navigate('/tarefas?status=concluida')}
                          className="cursor-pointer"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                          <YAxis allowDecimals={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar 
                            dataKey="value" 
                            fill={COLORS.tarefas} 
                            radius={[4, 4, 0, 0]}
                            className="hover:opacity-80 transition-opacity cursor-pointer"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {tarefasDistribuicaoData.length > 0 && (
                      <div className="h-64">
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          Distribuição de tarefas (SLA + Em aberto)
                        </p>
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={tarefasDistribuicaoData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                              labelLine={true}
                              onClick={handleTarefasClick}
                              className="cursor-pointer"
                            >
                              {tarefasDistribuicaoData.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={entry.color}
                                  className="hover:opacity-80 transition-opacity cursor-pointer"
                                />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </ChartSection>
              </CardContent>
            </Card>

            {/* Diárias Section - NEW */}
            <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  Total de Diárias
                </CardTitle>
                <CardDescription>
                  Dias de cuidado prestados no período (1 diária = 1 dia de internação)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-center p-6 bg-primary/10 rounded-lg flex-1">
                    <p className="text-5xl font-bold text-primary">{data.diarias?.total || 0}</p>
                    <p className="text-sm text-muted-foreground mt-2">diárias prestadas</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Primeiro dia de internação = D+1
                    </p>
                  </div>
                </div>
                
                <ChartSection title="Ver gráficos">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="h-64">
                      <p className="text-sm font-medium text-muted-foreground mb-2">
                        Diárias por dia ({appliedFilters.agrupamento === 'semana' ? 'semanal' : 'diário'})
                      </p>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={appliedFilters.agrupamento === 'semana' 
                            ? (data.diarias?.trend_semanal || [])
                            : (data.diarias?.trend || [])}
                          onClick={() => navigate('/lista')}
                          className="cursor-pointer"
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={60} />
                          <YAxis allowDecimals={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar 
                            dataKey="value" 
                            fill="#6366f1"
                            radius={[4, 4, 0, 0]}
                            className="hover:opacity-80 transition-opacity cursor-pointer"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    {data.diarias?.por_origem?.length > 0 && (
                      <div className="h-64">
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          Diárias por Origem
                        </p>
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={data.diarias.por_origem}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                              labelLine={true}
                              onClick={(e) => navigate(`/lista?origem=${e.key}`)}
                              className="cursor-pointer"
                            >
                              {data.diarias.por_origem.map((entry, index) => (
                                <Cell 
                                  key={`cell-origem-${index}`} 
                                  fill={ORIGEM_CHART_COLORS[entry.key] || COLORS.internacoes}
                                  className="hover:opacity-80 transition-opacity cursor-pointer"
                                />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </ChartSection>
              </CardContent>
            </Card>

            {/* Estoque Analytics Section */}
            {estoqueData && (
              <Card className="border-orange-200 dark:border-orange-800 bg-gradient-to-br from-orange-50/50 to-transparent dark:from-orange-900/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-orange-600" />
                    Análise de Estoque
                  </CardTitle>
                  <CardDescription>
                    Valor do inventário e consumo de materiais/medicamentos
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* KPI Cards */}
                  <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
                    <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <div className="flex items-center justify-center gap-1">
                        <DollarSign className="h-4 w-4 text-orange-600" />
                        <p className="text-2xl font-bold text-orange-600">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(estoqueData.valor_estoque_total || 0)}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Valor do Estoque</p>
                    </div>
                    
                    <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <div className="flex items-center justify-center gap-1">
                        <TrendingDown className="h-4 w-4 text-red-600" />
                        <p className="text-2xl font-bold text-red-600">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(estoqueData.consumo_total || 0)}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Consumo no Período</p>
                    </div>
                    
                    <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <div className="flex items-center justify-center gap-1">
                        <AlertTriangle className="h-4 w-4 text-amber-600" />
                        <p className="text-2xl font-bold text-amber-600">
                          {estoqueData.alertas?.critico || 0}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Itens Críticos</p>
                    </div>
                    
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{estoqueData.total_itens || 0}</p>
                      <p className="text-xs text-muted-foreground mt-1">Total de Itens</p>
                    </div>
                  </div>

                  {/* Consumption by Type */}
                  {estoqueData.consumo_por_tipo && (
                    <div className="grid gap-4 md:grid-cols-2 mb-4">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm font-medium mb-2">Consumo por Tipo:</p>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                              Materiais
                            </span>
                            <Badge variant="outline">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(estoqueData.consumo_por_tipo.material || 0)}
                            </Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                              Medicamentos
                            </span>
                            <Badge variant="outline">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(estoqueData.consumo_por_tipo.medicamento || 0)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      {estoqueData.valor_em_risco > 0 && (
                        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-red-700 dark:text-red-400">
                                Valor em Risco
                              </p>
                              <p className="text-2xl font-bold text-red-600">
                                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(estoqueData.valor_em_risco)}
                              </p>
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                Valor de itens em nível crítico de estoque
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <ChartSection title="Ver gráficos">
                    <div className="grid gap-4 md:grid-cols-2">
                      {/* Consumption Timeline */}
                      {estoqueData.consumo_timeline?.length > 0 && (
                        <div className="h-64">
                          <p className="text-sm font-medium text-muted-foreground mb-2">
                            Consumo ao Longo do Tempo (R$)
                          </p>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart 
                              data={estoqueData.consumo_timeline}
                              onClick={() => navigate('/estoque?tab=movimentacoes')}
                              className="cursor-pointer"
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="data" 
                                tick={{ fontSize: 10 }} 
                                angle={-45} 
                                textAnchor="end" 
                                height={60}
                                tickFormatter={(val) => val?.slice(5) || val}
                              />
                              <YAxis 
                                allowDecimals={false}
                                tickFormatter={(val) => `R$${val}`}
                              />
                              <Tooltip 
                                formatter={(value) => [new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value), 'Consumo']}
                                labelFormatter={(label) => `Data: ${label}`}
                              />
                              <Bar 
                                dataKey="valor" 
                                fill="#f97316"
                                radius={[4, 4, 0, 0]}
                                className="hover:opacity-80 transition-opacity cursor-pointer"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Top Items by Consumption */}
                      {estoqueData.top_itens_consumo?.length > 0 && (
                        <div className="h-64">
                          <p className="text-sm font-medium text-muted-foreground mb-2">
                            Top Itens por Consumo (R$)
                          </p>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart 
                              data={estoqueData.top_itens_consumo.slice(0, 5)}
                              layout="vertical"
                              onClick={() => navigate('/estoque')}
                              className="cursor-pointer"
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                type="number"
                                tickFormatter={(val) => `R$${val}`}
                              />
                              <YAxis 
                                type="category" 
                                dataKey="nome" 
                                tick={{ fontSize: 10 }}
                                width={100}
                              />
                              <Tooltip 
                                formatter={(value) => [new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value), 'Custo']}
                              />
                              <Bar 
                                dataKey="custo" 
                                fill="#8b5cf6"
                                radius={[0, 4, 4, 0]}
                                className="hover:opacity-80 transition-opacity cursor-pointer"
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>

                    {/* Top Patients by Consumption */}
                    {estoqueData.top_pacientes_consumo?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-muted-foreground mb-2">
                          Top Pacientes por Consumo
                        </p>
                        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                          {estoqueData.top_pacientes_consumo.slice(0, 6).map((pac, idx) => (
                            <div 
                              key={idx}
                              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                              onClick={() => pac.internacao_id && navigate(`/pacientes/${pac.internacao_id}`)}
                            >
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="w-6 h-6 flex items-center justify-center">
                                  {idx + 1}
                                </Badge>
                                <span className="text-sm font-medium truncate max-w-[120px]">{pac.nome}</span>
                              </div>
                              <span className="text-sm font-bold text-orange-600">
                                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pac.custo || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </ChartSection>
                </CardContent>
              </Card>
            )}

            {/* Serviços Analytics Section */}
            {servicosData && (
              <Card className="border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50/50 to-transparent dark:from-blue-900/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                        Análise de Serviços
                      </CardTitle>
                      <CardDescription>
                        {(canViewServiceRevenue || canViewServicePayout)
                          ? 'Receitas e custos de serviços profissionais (fisioterapia, enfermagem, etc.)'
                          : 'Quantidade de serviços profissionais (fisioterapia, enfermagem, etc.)'
                        }
                      </CardDescription>
                    </div>
                    {(canViewServiceRevenue || canViewServicePayout) && can('exportar_relatorios') && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/servicos/export?formato=csv&periodo=${appliedFilters.periodo}`, '_blank');
                          toast.success('Download CSV iniciado');
                        }}
                        data-testid="export-servicos-csv"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        CSV
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/servicos/export?formato=excel&periodo=${appliedFilters.periodo}`, '_blank');
                          toast.success('Download Excel iniciado');
                        }}
                        data-testid="export-servicos-excel"
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-1" />
                        Excel
                      </Button>
                    </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* KPI Cards */}
                  <div className={`grid gap-4 ${(canViewServiceRevenue || canViewServicePayout) ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-1'} mb-6`}>
                    <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">
                        {servicosData.resumo?.total_servicos_ativos || 0}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Serviços Ativos</p>
                    </div>
                    
                    {canViewServiceRevenue && (
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="flex items-center justify-center gap-1">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        <p className="text-2xl font-bold text-green-600">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(servicosData.resumo?.total_receita || 0)}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Receita Total</p>
                    </div>
                    )}
                    
                    {canViewServicePayout && (
                    <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <div className="flex items-center justify-center gap-1">
                        <TrendingDown className="h-4 w-4 text-red-600" />
                        <p className="text-2xl font-bold text-red-600">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', notation: 'compact' }).format(servicosData.resumo?.total_custo || 0)}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Custo Total</p>
                    </div>
                    )}
                    
                    {canViewServiceRevenue && canViewServicePayout && (
                    <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <p className={`text-2xl font-bold ${(servicosData.resumo?.margem || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {servicosData.resumo?.margem_percentual || 0}%
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Margem de Lucro</p>
                    </div>
                    )}
                  </div>

                  {/* Services by Type */}
                  {(canViewServiceRevenue || canViewServicePayout) && servicosData.por_tipo?.length > 0 && (
                    <ChartSection title="Ver detalhes por serviço">
                      <div className="mt-4">
                        <p className="text-sm font-medium text-muted-foreground mb-3">
                          {canViewServiceRevenue ? 'Receita por Tipo de Serviço' : 'Custo por Tipo de Serviço'}
                        </p>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={servicosData.por_tipo.slice(0, 8)}
                              layout="vertical"
                              margin={{ left: 80 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
                              <YAxis type="category" dataKey="nome" tick={{ fontSize: 12 }} width={75} />
                              <Tooltip 
                                formatter={(value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)}
                              />
                              {canViewServiceRevenue && (
                              <Bar 
                                dataKey="receita" 
                                fill="#22c55e"
                                radius={[0, 4, 4, 0]}
                                name="Receita"
                              />
                              )}
                              {canViewServicePayout && (
                              <Bar 
                                dataKey="custo" 
                                fill="#ef4444"
                                radius={[0, 4, 4, 0]}
                                name="Custo"
                              />
                              )}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Top Patients by Services */}
                      {canViewServiceRevenue && servicosData.por_paciente?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-muted-foreground mb-2">
                            Top Pacientes por Receita de Serviços
                          </p>
                          <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                            {servicosData.por_paciente.slice(0, 6).map((pac, idx) => (
                              <div 
                                key={idx}
                                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                              >
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="w-6 h-6 flex items-center justify-center">
                                    {idx + 1}
                                  </Badge>
                                  <span className="text-sm font-medium truncate max-w-[120px]">{pac.paciente_nome}</span>
                                </div>
                                <span className="text-sm font-bold text-green-600">
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pac.receita || 0)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </ChartSection>
                  )}

                  {!(canViewServiceRevenue || canViewServicePayout) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Valor restrito ao administrador master.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Relatório por Origem Section */}
            {origemData && (
              <Card className="border-cyan-200 dark:border-cyan-800 bg-gradient-to-br from-cyan-50/50 to-transparent dark:from-cyan-900/10">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-cyan-600" />
                        Relatório por Origem
                      </CardTitle>
                      <CardDescription>
                        Distribuição de pacientes por tipo de atendimento
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/internacoes/relatorio/por-origem/export?formato=csv`, '_blank');
                          toast.success('Download CSV iniciado');
                        }}
                        data-testid="export-origem-csv"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        CSV
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          window.open(`${process.env.REACT_APP_BACKEND_URL}/api/internacoes/relatorio/por-origem/export?formato=excel`, '_blank');
                          toast.success('Download Excel iniciado');
                        }}
                        data-testid="export-origem-excel"
                      >
                        <FileSpreadsheet className="h-4 w-4 mr-1" />
                        Excel
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* KPI Cards */}
                  <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
                    <div className="text-center p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-cyan-600">
                        {origemData.resumo?.total_internacoes || 0}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Total Internações</p>
                    </div>
                    
                    <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">
                        {origemData.resumo?.total_ativos || 0}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Ativos</p>
                    </div>
                    
                    <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">
                        {origemData.resumo?.origens_count || 0}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Origens Distintas</p>
                    </div>
                    
                    <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <div className="flex items-center justify-center gap-1">
                        <Clock className="h-4 w-4 text-orange-600" />
                        <p className="text-2xl font-bold text-orange-600">
                          {origemData.resumo?.media_dias_geral || 0}d
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Média de Dias</p>
                    </div>
                  </div>

                  {/* Origin Distribution Chart */}
                  {origemData.chart_data?.length > 0 && (
                    <ChartSection title="Ver distribuição por origem" defaultOpen={true}>
                      <div className="grid gap-4 md:grid-cols-2 mt-4">
                        {/* Pie Chart */}
                        <div className="h-72">
                          <p className="text-sm font-medium text-muted-foreground mb-2">
                            Distribuição por Origem
                          </p>
                          <ResponsiveContainer width="100%" height="100%">
                            <RechartsPieChart>
                              <Pie
                                data={origemData.chart_data}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                labelLine={true}
                                onClick={(e) => navigate(`/lista?origem=${e.key}`)}
                                className="cursor-pointer"
                              >
                                {origemData.chart_data.map((entry, index) => (
                                  <Cell 
                                    key={`cell-origem-report-${index}`} 
                                    fill={ORIGEM_CHART_COLORS[entry.key] || '#6b7280'}
                                    className="hover:opacity-80 transition-opacity cursor-pointer"
                                  />
                                ))}
                              </Pie>
                              <Tooltip content={<CustomTooltip />} />
                              <Legend />
                            </RechartsPieChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Bar Chart */}
                        <div className="h-72">
                          <p className="text-sm font-medium text-muted-foreground mb-2">
                            Pacientes por Origem
                          </p>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart 
                              data={origemData.por_origem?.slice(0, 8)}
                              layout="vertical"
                              onClick={(e) => e?.activePayload?.[0]?.payload && navigate(`/lista?origem=${e.activePayload[0].payload.origem}`)}
                              className="cursor-pointer"
                            >
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis 
                                type="category" 
                                dataKey="label" 
                                tick={{ fontSize: 11 }}
                                width={90}
                              />
                              <Tooltip 
                                formatter={(value, name) => [value, name === 'total' ? 'Pacientes' : name]}
                                content={({ active, payload }) => {
                                  if (active && payload?.length) {
                                    const d = payload[0].payload;
                                    return (
                                      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border">
                                        <p className="font-medium">{d.label}</p>
                                        <p className="text-sm">Pacientes: <span className="font-semibold">{d.total}</span></p>
                                        <p className="text-sm">Ativos: <span className="font-semibold text-green-600">{d.ativos}</span></p>
                                        <p className="text-sm">Média dias: <span className="font-semibold">{d.media_dias}d</span></p>
                                        <p className="text-xs text-blue-500 mt-1">Clique para ver lista</p>
                                      </div>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Bar 
                                dataKey="total" 
                                radius={[0, 4, 4, 0]}
                                className="hover:opacity-80 transition-opacity cursor-pointer"
                              >
                                {origemData.por_origem?.slice(0, 8).map((entry, index) => (
                                  <Cell 
                                    key={`bar-origem-${index}`} 
                                    fill={ORIGEM_CHART_COLORS[entry.origem] || '#6b7280'}
                                  />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Origin Details Cards */}
                      <div className="mt-6">
                        <p className="text-sm font-medium text-muted-foreground mb-3">
                          Detalhes por Origem
                        </p>
                        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                          {origemData.por_origem?.map((origem, idx) => (
                            <div 
                              key={idx}
                              className="p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow"
                              style={{ borderLeftColor: ORIGEM_CHART_COLORS[origem.origem] || '#6b7280', borderLeftWidth: '4px' }}
                              onClick={() => navigate(`/lista?origem=${origem.origem}`)}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium flex items-center gap-2">
                                  {origem.origem === 'pacs' && <Stethoscope className="h-4 w-4 text-cyan-600" />}
                                  {origem.origem === 'hmi' && <Baby className="h-4 w-4 text-amber-600" />}
                                  {origem.origem === 'cardiologia' && <Heart className="h-4 w-4 text-red-600" />}
                                  {origem.label}
                                </span>
                                <Badge variant="outline" style={{ color: ORIGEM_CHART_COLORS[origem.origem] }}>
                                  {origem.percentual}%
                                </Badge>
                              </div>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-center">
                                  <p className="font-bold text-lg">{origem.total}</p>
                                  <p className="text-muted-foreground">Total</p>
                                </div>
                                <div className="text-center">
                                  <p className="font-bold text-lg text-green-600">{origem.ativos}</p>
                                  <p className="text-muted-foreground">Ativos</p>
                                </div>
                                <div className="text-center">
                                  <p className="font-bold text-lg text-orange-600">{origem.media_dias}d</p>
                                  <p className="text-muted-foreground">Média</p>
                                </div>
                              </div>
                              {origem.medicos_solicitantes?.length > 0 && (
                                <div className="mt-3 pt-2 border-t text-xs space-y-1">
                                  <p className="text-muted-foreground font-medium">Médicos solicitantes:</p>
                                  {origem.medicos_solicitantes.map((med, mi) => (
                                    <div key={mi} className="flex items-center justify-between">
                                      <span>{med.nome}</span>
                                      <Badge variant="outline" className="text-xs">{med.total}</Badge>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </ChartSection>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Estatisticas;
